import {
  Box,
  Button,
  Card,
  Collapsible,
  Heading,
  HStack,
  Image,
  List,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArrowRight,
  BarChart3,
  Building2,
  Check,
  ChevronDown,
  Cpu,
  Database,
  Factory,
  Globe,
  Layers,
  Network,
  Radio,
  RefreshCw,
  Server,
  Share2,
  type Shield,
  Workflow,
  Zap,
} from 'lucide-react'

import mqttLogo from '@/assets/mqtt.png'
import sparkplugLogo from '@/assets/sparkplugb.png'
import { PageHeader, SectionHeading } from '@/components/shell'

export const Route = createFileRoute('/_app/education/uns')({
  component: UNSEducationPage,
})

interface FeatureCardProps {
  icon: typeof Shield
  title: string
  description: string
}

function FeatureCard({ icon: Icon, title, description }: FeatureCardProps) {
  return (
    <Card.Root transition="all 0.3s" _hover={{ shadow: 'md' }}>
      <Card.Body p={5} mt="4" mb="4">
        <VStack align="start" gap={3}>
          <Box p={2} bg="brand.solid" borderRadius="lg">
            <Icon size={20} />
          </Box>
          <VStack align="start" gap={1}>
            <Text fontWeight="bold">{title}</Text>
            <Text fontSize="sm" color="content.secondary">
              {description}
            </Text>
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

interface ConceptCardProps {
  image: string
  title: string
  children: React.ReactNode
  imageSize?: string
}

function ConceptCard({ image, title, children, imageSize = '120px' }: ConceptCardProps) {
  return (
    <Card.Root transition="all 0.3s" _hover={{ shadow: 'md' }}>
      <Card.Body p={6}>
        <VStack align="start" gap={4}>
          <Image src={image} alt={title} h={imageSize} objectFit="contain" />
          <VStack align="start" gap={2}>
            <Text fontWeight="bold" fontSize="lg">
              {title}
            </Text>
            {children}
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

function UNSEducationPage() {
  const navigate = useNavigate()

  const benefits = [
    {
      icon: Share2,
      title: 'Reduced Integration Costs',
      description: 'Eliminate expensive point-to-point connections by using a central data hub.',
    },
    {
      icon: Zap,
      title: 'Improved Agility',
      description:
        'Real-time data access enables faster decision-making and operational flexibility.',
    },
    {
      icon: Layers,
      title: 'Scalable Architecture',
      description:
        'Easily add new devices, systems, and applications without architectural changes.',
    },
    {
      icon: RefreshCw,
      title: 'Innovation Enablement',
      description: 'Unlock new applications and use cases with unified, accessible data.',
    },
  ]

  const technicalRequirements = [
    {
      icon: Globe,
      title: 'Open Architecture',
      description:
        'MQTT and Sparkplug specifications are publicly available, enabling vendor interoperability.',
    },
    {
      icon: Radio,
      title: 'Edge-Driven',
      description:
        'Data flows directly from edge devices into the central broker as single source of truth.',
    },
    {
      icon: Zap,
      title: 'Lightweight Protocol',
      description:
        'Binary format minimizes message size and reduces network bandwidth consumption.',
    },
    {
      icon: RefreshCw,
      title: 'Report by Exception',
      description: 'Publish data only when values change, eliminating continuous polling overhead.',
    },
  ]

  const useCases = [
    {
      icon: Factory,
      title: 'Production Order Status',
      description: 'Consolidate data from ERP and shop-floor systems for real-time visibility.',
    },
    {
      icon: Workflow,
      title: 'Shop-Floor Scheduling',
      description: 'Connect ERP, APS, and MES systems for optimized production planning.',
    },
    {
      icon: Building2,
      title: 'Warehouse Operations',
      description: 'Integrate WMS with production systems for seamless logistics.',
    },
    {
      icon: BarChart3,
      title: 'Real-Time OEE Tracking',
      description: 'Monitor Overall Equipment Effectiveness across all production lines.',
    },
    {
      icon: Database,
      title: 'Digital Work Orders',
      description: 'Connect PLCs and operations for paperless manufacturing.',
    },
    {
      icon: Cpu,
      title: 'Predictive Maintenance',
      description: 'Aggregate sensor data for AI-driven equipment health monitoring.',
    },
  ]

  const hierarchyLevels = [
    { level: 'Enterprise', example: 'FreshDairy', description: 'Top-level organization' },
    { level: 'Site', example: 'Munich', description: 'Physical location or plant' },
    { level: 'Area', example: 'Packaging', description: 'Functional area within site' },
    { level: 'Line', example: 'Line1', description: 'Production line' },
    { level: 'Cell', example: 'Cell1', description: 'Work cell or machine' },
  ]

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="Unified Namespace (UNS)"
        subtitle="The modern architecture for Industrial IoT data integration and real-time communication"
      />

      <VStack align="stretch" gap={10}>
        {/* Hero identity */}
        <VStack gap={5} textAlign="center" align="center">
          <Box p={4} bg="brand.solid" borderRadius="2xl">
            <Network size={48} />
          </Box>
        </VStack>

        {/* Introduction Section */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="What is a Unified Namespace?" />
          <Collapsible.Root w="100%">
            <Card.Root w="100%">
              <Collapsible.Trigger asChild>
                <Card.Header
                  p={4}
                  cursor="pointer"
                  _hover={{ bg: 'gray.50', _dark: { bg: 'gray.700' } }}
                  borderRadius="lg"
                >
                  <HStack justify="space-between" w="100%">
                    <Text fontWeight="bold" fontSize="lg">
                      Learn about UNS
                    </Text>
                    <Collapsible.Indicator
                      transition="transform 0.2s"
                      _open={{ transform: 'rotate(180deg)' }}
                    >
                      <ChevronDown size={20} />
                    </Collapsible.Indicator>
                  </HStack>
                </Card.Header>
              </Collapsible.Trigger>
              <Collapsible.Content>
                <Card.Body p={6} pt={2} mb="4">
                  <VStack align="start" gap={4}>
                    <Text fontSize="md">
                      <strong>
                        The Unified Namespace is the structure and events of your business
                      </strong>
                      , serving as a central hub through which all your IIoT components communicate.
                      It enables organizations to collect data from various industrial IoT systems,
                      contextualize it, and transform it into formats that other systems can
                      understand.
                    </Text>
                    <Text fontSize="md">
                      Unlike traditional point-to-point integrations that create complex "spaghetti"
                      architectures, UNS provides a <strong>single source of truth</strong> for all
                      operational data. The UNS sits between edge devices and enterprise systems,
                      integrating SCADA, Historians, MES, ERP, and advanced analytics platforms
                      through a unified interface.
                    </Text>
                    <Text fontSize="md">
                      The key principle:{' '}
                      <strong>
                        "Connect your devices to infrastructure, not to applications."
                      </strong>{' '}
                      This approach enables superior OT solutions while providing a single,
                      consistent view of all operational data across your enterprise.
                    </Text>
                  </VStack>
                </Card.Body>
              </Collapsible.Content>
            </Card.Root>
          </Collapsible.Root>
        </VStack>

        {/* Benefits Section */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Key Benefits of UNS"
            description="Transform your industrial operations with a unified data architecture"
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
            {benefits.map((benefit) => (
              <FeatureCard key={benefit.title} {...benefit} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Why MQTT for UNS */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Why MQTT for Unified Namespace?"
            description="MQTT provides the essential capabilities required for a successful UNS implementation"
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
            {technicalRequirements.map((req) => (
              <FeatureCard key={req.title} {...req} />
            ))}
          </SimpleGrid>

          <Card.Root w="100%">
            <Card.Body p={6} mt="4" mb="4">
              <VStack align="start" gap={4}>
                <Text fontWeight="bold" fontSize="lg">
                  MQTT as the Central Hub
                </Text>
                <Text fontSize="md">
                  The MQTT broker serves as the central hub for data communication in a UNS
                  architecture. It provides:
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="100%">
                  <List.Root gap={2}>
                    <List.Item>
                      <HStack gap={2}>
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                        <Text fontSize="sm">
                          Publish-subscribe messaging for seamless data exchange
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                        <Text fontSize="sm">Real-time, event-driven communication at scale</Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                        <Text fontSize="sm">
                          Hierarchical topic structures for semantic organization
                        </Text>
                      </HStack>
                    </List.Item>
                  </List.Root>
                  <List.Root gap={2}>
                    <List.Item>
                      <HStack gap={2}>
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                        <Text fontSize="sm">Flexible routing and distribution patterns</Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                        <Text fontSize="sm">Decoupled producers and consumers</Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                        <Text fontSize="sm">Support for multiple broker deployment</Text>
                      </HStack>
                    </List.Item>
                  </List.Root>
                </SimpleGrid>
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* ISA-95 Hierarchy */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="ISA-95 Semantic Hierarchy"
            description="Organize your MQTT topic structure using the ISA-95 common data model"
          />
          <Card.Root w="100%">
            <Card.Body p={6} mt="4" mb="4">
              <VStack align="start" gap={4}>
                <Text fontWeight="bold" fontSize="lg">
                  Topic Hierarchy Structure
                </Text>
                <Box
                  bg="gray.100"
                  _dark={{ bg: 'gray.700' }}
                  p={4}
                  borderRadius="lg"
                  w="100%"
                  fontFamily="mono"
                >
                  <Text fontSize="sm">Enterprise / Site / Area / Line / Cell</Text>
                </Box>

                <SimpleGrid columns={{ base: 1, md: 2 }} gap={6} w="100%">
                  <VStack align="start" gap={3}>
                    <Text fontWeight="bold">Hierarchy Levels:</Text>
                    {hierarchyLevels.map((item) => (
                      <HStack key={item.level} gap={3} w="100%">
                        <Box
                          bg="brand.solid"
                          px={2}
                          py={1}
                          borderRadius="md"
                          minW="80px"
                          textAlign="center"
                        >
                          <Text fontSize="xs" fontWeight="bold">
                            {item.level}
                          </Text>
                        </Box>
                        <Text fontSize="sm" color="content.secondary">
                          {item.description}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                  <VStack align="start" gap={3}>
                    <Text fontWeight="bold">Example Topic:</Text>
                    <Box
                      bg="gray.100"
                      _dark={{ bg: 'gray.700' }}
                      p={3}
                      borderRadius="lg"
                      w="100%"
                      fontFamily="mono"
                    >
                      <Text fontSize="sm">FreshDairy/Munich/Packaging/Line1/Cell1</Text>
                    </Box>
                    <Text fontSize="sm" color="content.secondary">
                      This structure ensures information about data access is available independent
                      of actual data content, maintaining consistency across the UNS.
                    </Text>
                    <Text fontWeight="bold" mt={2}>
                      Namespace Types:
                    </Text>
                    <List.Root gap={1}>
                      <List.Item>
                        <Text fontSize="sm">
                          <strong>Edge Namespace:</strong> Raw PLC data
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text fontSize="sm">
                          <strong>Functional Namespace:</strong> Processed, contextualized data
                        </Text>
                      </List.Item>
                    </List.Root>
                  </VStack>
                </SimpleGrid>
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* Sparkplug Integration */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Sparkplug for UNS"
            description="The standardized implementation for payload structure enforcement"
          />
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
            <ConceptCard image={sparkplugLogo} title="Why Sparkplug B?" imageSize="80px">
              <Text fontSize="sm" color="content.secondary" mb={2}>
                Sparkplug B is the ISO standard that provides:
              </Text>
              <List.Root gap={1}>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">Consistent MQTT topic namespace</Text>
                  </HStack>
                </List.Item>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">Auto-discovery of devices</Text>
                  </HStack>
                </List.Item>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">Data-type definitions</Text>
                  </HStack>
                </List.Item>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">State management</Text>
                  </HStack>
                </List.Item>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">Birth/Death certificates</Text>
                  </HStack>
                </List.Item>
              </List.Root>
            </ConceptCard>

            <ConceptCard image={mqttLogo} title="Topic Format" imageSize="80px">
              <Text fontSize="sm" color="content.secondary" mb={2}>
                Standard Sparkplug topic structure:
              </Text>
              <Box
                bg="gray.100"
                _dark={{ bg: 'gray.700' }}
                p={2}
                borderRadius="md"
                fontFamily="mono"
                mb={3}
              >
                <Text fontSize="xs">spBv1.0/group_id/message_type/edge_node_id/[device_id]</Text>
              </Box>
              <Text fontSize="sm" color="content.secondary" mb={2}>
                Parris Method (recommended):
              </Text>
              <Box
                bg="gray.100"
                _dark={{ bg: 'gray.700' }}
                p={2}
                borderRadius="md"
                fontFamily="mono"
              >
                <Text fontSize="xs">spBv1.0/Plant1:Area3:Line4:Cell2/NDATA/edge_node</Text>
              </Box>
              <Text fontSize="xs" color="content.secondary" mt={2}>
                Embeds enterprise structure in GroupID for globally unique identification.
              </Text>
            </ConceptCard>
          </SimpleGrid>
        </VStack>

        {/* Use Cases */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="UNS Use Cases"
            description="Real-world applications of Unified Namespace in industrial environments"
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
            {useCases.map((useCase) => (
              <FeatureCard key={useCase.title} {...useCase} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Data Persistence */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Data Persistence"
            description="Essential components for an effective UNS architecture"
          />
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
            <Card.Root>
              <Card.Body p={6} mt="4" mb="4">
                <VStack align="start" gap={3}>
                  <HStack gap={3}>
                    <Box p={2} bg="brand.solid" borderRadius="lg">
                      <Database size={20} />
                    </Box>
                    <Text fontWeight="bold" fontSize="lg">
                      Historian (Time-Series DB)
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color="content.secondary">
                    Archives data over time, allowing for retrospective analysis. The historian
                    mirrors the live data model of the UNS, providing a historical record accessible
                    through the IIoT platform.
                  </Text>
                  <List.Root gap={1}>
                    <List.Item>
                      <HStack gap={2}>
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                        <Text fontSize="sm">Time-series data storage</Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                        <Text fontSize="sm">Trend analysis capabilities</Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                        <Text fontSize="sm">Historical playback</Text>
                      </HStack>
                    </List.Item>
                  </List.Root>
                </VStack>
              </Card.Body>
            </Card.Root>

            <Card.Root>
              <Card.Body p={6} mt="4" mb="4">
                <VStack align="start" gap={3}>
                  <HStack gap={3}>
                    <Box p={2} bg="brand.solid" borderRadius="lg">
                      <Server size={20} />
                    </Box>
                    <Text fontWeight="bold" fontSize="lg">
                      Structured Database (SQL)
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color="content.secondary">
                    Holds structured data for operational management, including work orders, product
                    information, and configuration data that needs relational querying.
                  </Text>
                  <List.Root gap={1}>
                    <List.Item>
                      <HStack gap={2}>
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                        <Text fontSize="sm">Relational data storage</Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                        <Text fontSize="sm">Complex querying support</Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                        <Text fontSize="sm">Transactional integrity</Text>
                      </HStack>
                    </List.Item>
                  </List.Root>
                </VStack>
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* CTA */}
        <Card.Root bg="bg.subtle">
          <Card.Body p={8}>
            <HStack justify="space-between" align="center" flexWrap="wrap" gap={4}>
              <VStack align="start" gap={1}>
                <Heading size="lg">Learn About Sparkplug</Heading>
                <Text color="content.secondary">
                  Discover how Sparkplug B standardizes UNS implementations
                </Text>
              </VStack>
              <Button size="lg" onClick={() => navigate({ to: '/education/sparkplug' })}>
                Explore Sparkplug
                <ArrowRight size={18} />
              </Button>
            </HStack>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  )
}
